import { EventBus } from "pdfjs-dist/web/pdf_viewer.mjs";
// import { CursorTool } from "pdfjs-dist/lib/web/pdf_cursor_tools";
import { BaseBrush, IEvent, ITextOptions } from "fabric/fabric-impl";
import { fabric } from "fabric";
import { debounce } from "lodash";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore, userInfoStore } from "Stores";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import i18n, { I18N_NS } from "Config/i18n";
import { modal } from "Components/General/Modal/AntdModal";
import { CursorTool } from "./uiUtils";
import hashSum from "hash-sum";
import { until } from "utils/until";
import { notificationError } from "utils/libHelpers/AntdNotification";
import {
  getPdfPageViewRotation,
  resolveStoredCanvasSize,
  transformSerializedAnnotationCanvas,
} from "./PDFAnnotationTransforms";
import type { TransformSerializedAnnotationCanvasOptions } from "./PDFAnnotationTransforms";

export enum AnnotationTool {
  TEXT = "TEXT",
  RECT = "RECT",
  LINE = "LINE",
  PENCIL = "PENCIL",
  CHECK = "CHECK",
  CROSS = "CROSS",
  ERASER = "ERASER",
  HIGHLIGHTER = "HIGHLIGHTER",
  SIGNATURE = "SIGNATURE",
  MERGE = "MERGE",
}

interface ExtendedFabricCanvasOptions extends fabric.ICanvasOptions {
  scale: number;
  rotation: number;
}

interface ExtendedFabricCanvas extends fabric.Canvas {
  scale: number;
  rotation: number;
  pageNumber: number;
  pageWidth: number;
  pageHeight: number;
  /** False until pagerendered finishes loadFromJSON for this canvas. */
  annotationsReady?: boolean;
}

interface FetchOptions {
  workListId?: number;
  archivedFileId?: number;
}

interface BrushParams extends Partial<Pick<BaseBrush, "width" | "color">> {}

interface AnnotationSettingsOptions {
  fill: string;
  fontSize: number;
  fontFamily: string;
  highlightColor: string;
  highlightWidth: number;
  pencilWidth: number;
}

export const DEFAULT_TEXT_OPTS = {
  fill: "#333",
  fontSize: 10,
  fontFamily: "Roboto",
  highlightColor: "rgba(228, 51, 42, 0.6)",
  highlightWidth: 20,
  pencilWidth: 5,
};

export class AnnotationSettings {
  static fill: string;
  static fontSize: number;
  static fontFamily: string;
  static highlightColor: string = "rgba(228, 51, 42, 0.6)";
  static highlightWidth: number = 20;
  static pencilWidth: number = 5;

  private static setup(options: AnnotationSettingsOptions) {
    AnnotationSettings.fill = (options.fill as string) || DEFAULT_TEXT_OPTS.fill;
    AnnotationSettings.fontSize = options.fontSize || DEFAULT_TEXT_OPTS.fontSize;
    AnnotationSettings.fontFamily = options.fontFamily || DEFAULT_TEXT_OPTS.fontFamily;
    AnnotationSettings.highlightColor = options.highlightColor || DEFAULT_TEXT_OPTS.highlightColor;
    AnnotationSettings.highlightWidth = options.highlightWidth || DEFAULT_TEXT_OPTS.highlightWidth;
    AnnotationSettings.pencilWidth = options.pencilWidth || DEFAULT_TEXT_OPTS.pencilWidth;
  }

  public static setupFromLocalStorage() {
    const namespace = `${userInfoStore.userID}_annotationSettings`;
    const storedTextToolOptions = localStorage.getItem(namespace);
    if (storedTextToolOptions) {
      try {
        const parsedTextToolOptions = JSON.parse(storedTextToolOptions);
        this.setup({ ...DEFAULT_TEXT_OPTS, ...parsedTextToolOptions });
      } catch (error) {
        this.setup(DEFAULT_TEXT_OPTS);
      }
    } else {
      this.setup(DEFAULT_TEXT_OPTS);
    }
  }

  public static getOptions(): AnnotationSettingsOptions {
    return {
      fill: AnnotationSettings.fill,
      fontSize: AnnotationSettings.fontSize,
      fontFamily: AnnotationSettings.fontFamily,
      highlightColor: AnnotationSettings.highlightColor,
      highlightWidth: AnnotationSettings.highlightWidth,
      pencilWidth: AnnotationSettings.pencilWidth,
    };
  }
}

export class PDFAnnotation {
  fabricPages: Map<number, ExtendedFabricCanvas>; // <pageNumber, Fabric Canvas>
  eventBus: EventBus;
  activeTool: AnnotationTool = null;
  selectedCanvasPageNumber: number = null; // used in copy and paste feature
  selectedCanvasObject: fabric.Object = null; // used in copy and paste feature
  mousePosition: { pageNumber: number; pointer: { x: number; y: number } }; // used in copy and paste feature
  annotationSettings = AnnotationSettings; // used in text tool
  highlightingParams: BrushParams = {
    width: 20,
    color: "rgba(228, 51, 42, 0.6)",
  };
  annotationsDb = {};
  annotationsHash: string = null;
  enabled = false;
  worklistId: number = null;
  _storedSignature: fabric.Object = null;
  _fileId: number;
  _archivedFileId: number;
  fetchOptions: FetchOptions = {};
  loading = false;
  private boundKeyboardEventListener: (e: KeyboardEvent) => void;
  /** In-flight pagerendered Fabric rebuilds; autosave waits until this returns to 0. */
  private canvasRebuildDepth = 0;
  private pendingPreservePages = new Set<number>();
  private debouncedPreservePage: number | null = null;

  private debouncePreserveChanges;
  constructor(eventBus: EventBus) {
    this.initAnnotationSettings();
    this.eventBus = eventBus;
    this.fabricPages = new Map();
    this.eventBus._on("pagerendered", this.renderCanvasForPage.bind(this));
    this.eventBus._on("setannotationtool", this.setTool.bind(this));
    this.eventBus._on("resetannotationtool", this.resetTool.bind(this));
    this.eventBus._on("mergeAnnotations", this.mergeAnnotations.bind(this));
    this.eventBus._on("switchcursortool", this.turnOff.bind(this));
    this.eventBus._on("enableannotationmode", this.setAnnotationMode.bind(this, true));
    this.eventBus._on("changetexttool", this.setTextToolOptions.bind(this));
    this.debouncePreserveChanges = debounce(this._preserveChanges.bind(this), 800);
    this.boundKeyboardEventListener = this.keyboardEventListener.bind(this);
  }

  getUserAnnotationNamespace() {
    return `${userInfoStore.userID}_annotationSettings`;
  }

  initAnnotationSettings() {
    this.annotationSettings.setupFromLocalStorage();
  }

  saveAnnotationSettings() {
    const namespace = this.getUserAnnotationNamespace();
    localStorage.setItem(namespace, JSON.stringify(this.annotationSettings.getOptions()));
  }

  async getAnnotations(fileId: number, opts?: FetchOptions) {
    try {
      this.fetchOptions = opts;
      let response;
      if (opts.archivedFileId) {
        response = await API.getData(
          API_URL.ANNOTATIONS.getPdfAnnotationsArchivFile(projectsDataStore.projectId, fileId, opts.archivedFileId),
          {
            worklistId: this.worklistId,
          }
        );
      } else {
        response = await API.getData(API_URL.ANNOTATIONS.getPdfAnnotations(projectsDataStore.projectId, fileId), {
          worklistId: this.worklistId,
        });
      }
      this.annotationsDb = response || {};
    } catch (error) {
      throw error;
    }
  }

  refetchAnnotations() {
    return this.getAnnotations(this._fileId, this.fetchOptions);
  }

  async initAnnotations(fileId: number, opts?: FetchOptions) {
    this._fileId = fileId;
    this._archivedFileId = opts.archivedFileId;
    this.worklistId = opts.workListId;
    await this.getAnnotations(fileId, opts);
    this.fabricPages.forEach((fabricCanvas, pageNumber) => {
      const storedAnnotations = this._getStoredAnnotationsForPage(pageNumber);
      if (storedAnnotations) {
        fabricCanvas.loadFromJSON(this._getSerializedObjectsForDisplay(storedAnnotations, fabricCanvas), null);
      }
    });
  }

  _pageAnnotationsKey(pageNumber: number): string {
    return String(pageNumber);
  }

  _getStoredAnnotationsForPage(pageNumber: number) {
    const key = this._pageAnnotationsKey(pageNumber);
    return this.annotationsDb[key] ?? this.annotationsDb[pageNumber];
  }

  _setStoredAnnotationsForPage(pageNumber: number, serializedPageNormalized: Record<string, unknown>) {
    this.annotationsDb[this._pageAnnotationsKey(pageNumber)] = serializedPageNormalized;
  }

  _normalizeLiveCanvasForStorage(fabricPage: ExtendedFabricCanvas) {
    const pageScale = fabricPage.scale;
    const pageRotation = fabricPage.rotation;
    const deltaScale = 1 / pageScale;
    const deltaRotation = pageRotation === 0 ? 0 : 360 - pageRotation;
    const serializedPage = fabricPage.toObject(["uid"]);
    return this._getTransformedSerializedObjects(serializedPage, deltaScale, deltaRotation, {
      width: fabricPage.width,
      height: fabricPage.height,
    });
  }

  handleCanvasAfterRender(pageNumber: number, event: { ctx?: CanvasRenderingContext2D }) {
    if (this.canvasRebuildDepth > 0) {
      return;
    }
    this.debouncedPreservePage = pageNumber;
    this.debouncePreserveChanges(pageNumber, event);
  }

  private finishCanvasRebuild(newCanvas: ExtendedFabricCanvas) {
    newCanvas.annotationsReady = true;
    newCanvas.renderAll();
    this.canvasRebuildDepth = Math.max(0, this.canvasRebuildDepth - 1);
    if (this.canvasRebuildDepth !== 0 || this.pendingPreservePages.size === 0) {
      return;
    }
    const pagesToPreserve = [...this.pendingPreservePages];
    this.pendingPreservePages.clear();
    for (const pageNumber of pagesToPreserve) {
      const fabricPage = this.fabricPages.get(pageNumber);
      if (fabricPage?.annotationsReady !== false) {
        fabricPage.renderAll();
      } else {
        this.pendingPreservePages.add(pageNumber);
      }
    }
  }

  renderCanvasForPage(data) {
    if (data.isDetailView) {
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = data.source.canvas.clientWidth;
    canvas.height = data.source.canvas.clientHeight;
    const scale = data.source.scale;
    const rotation = getPdfPageViewRotation(data.source);
    const existingCanvasContainer = data.source.div?.querySelector(".canvas-container");
    if (existingCanvasContainer) {
      existingCanvasContainer.remove();
    }
    data.source.div.appendChild(canvas);
    const newCanvas = new fabric.Canvas(canvas, {
      selectionLineWidth: 2,
      scale: scale,
      rotation: rotation,
      pageNumber: data.pageNumber,
    } as ExtendedFabricCanvasOptions) as ExtendedFabricCanvas;
    newCanvas.annotationsReady = false;
    newCanvas.on("mouse:down", this.clickHandler.bind(this, data.pageNumber));
    newCanvas.on("mouse:move", this.trackMouseMove.bind(this, data.pageNumber));
    newCanvas.on("after:render", this.handleCanvasAfterRender.bind(this, data.pageNumber));

    newCanvas.on("selection:created", ({ selected: _ }) => {
      this.fabricPages.forEach((page) => {
        if (data.pageNumber !== page.pageNumber) {
          page.discardActiveObject();
          page.renderAll();
        }
      });
      this.selectedCanvasPageNumber = data.pageNumber;
    });
    newCanvas.on("selection:updated", ({ selected: _1, deselected: _2 }) => {
      this.selectedCanvasPageNumber = data.pageNumber;
      this.fabricPages.forEach((page) => {
        if (data.pageNumber !== page.pageNumber) {
          page.discardActiveObject();
          page.renderAll();
        }
      });
    });
    this.canvasRebuildDepth += 1;
    if (this.debouncedPreservePage !== null) {
      this.pendingPreservePages.add(this.debouncedPreservePage);
    }
    this.debouncePreserveChanges.cancel();

    const existingCanvas = this.fabricPages.get(data.pageNumber);
    const finishCanvasSetup = () => {
      this.finishCanvasRebuild(newCanvas);
    };

    if (existingCanvas) {
      const canSnapshotLiveCanvas = existingCanvas.annotationsReady !== false;
      if (canSnapshotLiveCanvas) {
        this._setStoredAnnotationsForPage(data.pageNumber, this._normalizeLiveCanvasForStorage(existingCanvas));
        this.pendingPreservePages.add(data.pageNumber);
      }
      const storedAnnotations = this._getStoredAnnotationsForPage(data.pageNumber);
      if (storedAnnotations?.objects?.length) {
        newCanvas.loadFromJSON(this._getSerializedObjectsForDisplay(storedAnnotations, newCanvas), finishCanvasSetup);
      } else {
        finishCanvasSetup();
      }
      existingCanvas.removeListeners();
    } else {
      const storedAnnotations = this._getStoredAnnotationsForPage(data.pageNumber);
      if (storedAnnotations?.objects?.length) {
        newCanvas.loadFromJSON(this._getSerializedObjectsForDisplay(storedAnnotations, newCanvas), finishCanvasSetup);
      } else {
        finishCanvasSetup();
      }
    }
    this.fabricPages.set(data.pageNumber, newCanvas as ExtendedFabricCanvas);
  }

  trackMouseMove(pageNumber, { pointer }) {
    this.mousePosition = { pageNumber: pageNumber, pointer };
  }

  get hasAnnotations() {
    return (
      Object.keys(this.annotationsDb).length > 0 &&
      Object.keys(this.annotationsDb).some((key) => this.annotationsDb[key]?.objects?.length > 0)
    );
  }

  getHasAnnotations() {
    return this.hasAnnotations;
  }

  clickHandler(pageNumber: number, event: IEvent<MouseEvent>) {
    // console.log("event", event);
    const disposeTool = () => {
      if (!event.e.ctrlKey && !event.e.shiftKey) {
        this.setTool({ tool: this.activeTool }); // unselect active tool
      }
    };
    if (event.target) {
      return;
    }
    const fabricObj = this.fabricPages.get(pageNumber);
    const activeObjects = fabricObj.getActiveObjects().length;
    if (activeObjects) {
      fabricObj.discardActiveObject();
      fabricObj.renderAll();
      return;
    }
    const initPos = { x: event.pointer.x, y: event.pointer.y };
    let toolObj;
    switch (this.activeTool) {
      case AnnotationTool.TEXT: {
        const upperCanvasEl = (fabricObj as ExtendedFabricCanvas & { upperCanvasEl?: HTMLCanvasElement }).upperCanvasEl;
        const modalContainer = upperCanvasEl?.closest(".viewer-modal") as HTMLElement | null;
        const pageScale = fabricObj.scale;
        const textOptions: ITextOptions & { hiddenTextareaContainer?: HTMLElement } = {
          fill: this.annotationSettings.fill,
          fontSize: this.annotationSettings.fontSize * pageScale,
          fontFamily: this.annotationSettings.fontFamily,
          left: event.pointer.x,
          top: event.pointer.y,
        };
        // Use custom hidden textarea container only in modal mode.
        // In split view it can change scroll dimensions and shift viewport/focus.
        if (modalContainer) {
          textOptions.hiddenTextareaContainer = modalContainer;
        }
        toolObj = new fabric.IText("text", textOptions as ITextOptions);
        fabricObj.add(toolObj);
        fabricObj.setActiveObject(toolObj);
        fabricObj.requestRenderAll();
        toolObj.selectAll();
        toolObj.enterEditing();
        window.setTimeout(() => {
          (toolObj as fabric.IText & { hiddenTextarea?: HTMLTextAreaElement }).hiddenTextarea?.focus();
        }, 0);
        disposeTool();
        break;
      }
      case AnnotationTool.RECT:
        toolObj = new fabric.Rect({
          width: 0,
          height: 0,
          strokeWidth: 3,
          stroke: this.annotationSettings.fill,
          fill: "",
          left: event.pointer.x,
          top: event.pointer.y,
          strokeUniform: false,
        });
        fabricObj.add(toolObj);
        fabricObj.on("mouse:move", ({ pointer }) => {
          toolObj.set({
            width: Math.abs(initPos.x - pointer.x),
            height: Math.abs(initPos.y - pointer.y),
          });
          toolObj.set({ left: Math.min(pointer.x, initPos.x) });
          toolObj.set({ top: Math.min(pointer.y, initPos.y) });
          toolObj.setCoords();
        });
        fabricObj.on("mouse:up", () => {
          if (toolObj.width < 5 && toolObj.height < 5) {
            fabricObj.remove(toolObj);
          }
          fabricObj.off("mouse:move");
          fabricObj.on("mouse:move", this.trackMouseMove.bind(this, pageNumber));
          fabricObj.off("mouse:up");
          disposeTool();
        });
        break;
      case AnnotationTool.LINE:
        {
          const lineOpts = {
            strokeWidth: 3,
            stroke: this.annotationSettings.fill,
            selectable: true,
            strokeUniform: false,
          };
          const line = new fabric.Line([initPos.x, initPos.y], lineOpts);
          fabricObj.add(line);
          fabricObj.selection = false;
          fabricObj.on("mouse:move", ({ pointer }) => {
            line
              .set({
                x2: pointer.x,
                y2: pointer.y,
              })
              .setCoords();
            fabricObj.requestRenderAll();
          });
          fabricObj.on("mouse:up", () => {
            if (!line.x2 || !line.y2) {
              fabricObj.remove(line);
            }
            fabricObj.selection = true;

            fabricObj.off("mouse:move");
            fabricObj.on("mouse:move", this.trackMouseMove.bind(this, pageNumber));
            fabricObj.off("mouse:up");
            disposeTool();
            fabricObj.renderAll();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this._preserveChanges(pageNumber, { ctx: (fabricObj as any).contextTop });
          });
        }
        break;
      case AnnotationTool.CHECK:
        toolObj = new fabric.Path(
          // eslint-disable-next-line max-len
          "M 27.5 7.53125 L 24.464844 4.542969 C 24.15625 4.238281 23.65625 4.238281 23.347656 4.542969 L 11.035156 16.667969 L 6.824219 12.523438 C 6.527344 12.230469 6 12.230469 5.703125 12.523438 L 2.640625 15.539062 C 2.332031 15.84375 2.332031 16.335938 2.640625 16.640625 L 10.445312 24.324219 C 10.59375 24.472656 10.796875 24.554688 11.007812 24.554688 C 11.214844 24.554688 11.417969 24.472656 11.566406 24.324219 L 27.5 8.632812 C 27.648438 8.488281 27.734375 8.289062 27.734375 8.082031 C 27.734375 7.875 27.648438 7.679688 27.5 7.53125 Z M 27.5 7.53125",
          { left: event.pointer.x, top: event.pointer.y, fill: "#188038" }
        );
        fabricObj.add(toolObj);
        disposeTool();
        break;
      case AnnotationTool.CROSS:
        toolObj = new fabric.Path(
          // eslint-disable-next-line max-len
          "M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z",
          { left: event.pointer.x, top: event.pointer.y, fill: "#f52222" }
        );
        (toolObj as fabric.Path).scale(4);
        fabricObj.add(toolObj);
        disposeTool();
        break;
      case AnnotationTool.PENCIL:
        fabricObj.isDrawingMode = true;
        fabricObj.freeDrawingBrush.width = this.annotationSettings.pencilWidth;
        fabricObj.freeDrawingBrush.color = this.annotationSettings.fill;
        break;
      case AnnotationTool.ERASER:
        fabricObj.isDrawingMode = true;
        fabricObj.freeDrawingBrush.width = this.annotationSettings.pencilWidth;
        fabricObj.freeDrawingBrush.color = this.annotationSettings.fill;
        break;
      case AnnotationTool.HIGHLIGHTER:
        fabricObj.isDrawingMode = true;
        fabricObj.freeDrawingBrush.width = this.annotationSettings.highlightWidth;
        fabricObj.freeDrawingBrush.color = this.annotationSettings.highlightColor;
        break;
      case AnnotationTool.SIGNATURE:
        if (this._storedSignature) {
          this._storedSignature.clone((cloned) => {
            toolObj = cloned;
            const signatureObj: fabric.Object = toolObj;
            signatureObj.left = event.pointer.x;
            signatureObj.top = event.pointer.y;
            signatureObj.lockScalingFlip = true;
            signatureObj.lockSkewingX = true;
            signatureObj.lockSkewingY = true;
            signatureObj.lockUniScaling = true;
            fabricObj.add(signatureObj);
          });
        }
        disposeTool();
        break;
      default:
        break;
    }
  }

  // serializes to initial scale and saves annotations after it was changed
  async _preserveChanges(pageNumber: number, { ctx }) {
    this.debouncedPreservePage = null;
    if (!ctx) {
      return;
    }
    if (!pageNumber || isNaN(pageNumber)) {
      throw new Error("Page number was not found");
    }

    try {
      this.loading = true;
      const fabricPage = this.fabricPages.get(pageNumber);

      const pageScale = fabricPage.scale;
      const pageRotation = fabricPage.rotation;
      const deltaScale = 1 / pageScale;
      const deltaRotation = pageRotation === 0 ? 0 : 360 - pageRotation;
      const serializedPage = fabricPage.toObject(["uid"]);
      // The objects come from fabricPage, so its own dimensions are the space they live in. The rendered
      // element is not: it reports a size of 0 once pdf.js drops the page view, and it can belong to an
      // already replaced canvas when a zoom change lands inside the debounce window.
      const serializedPageNormalized = this._getTransformedSerializedObjects(
        serializedPage,
        deltaScale,
        deltaRotation,
        {
          width: fabricPage.width,
          height: fabricPage.height,
        }
      );
      this._setStoredAnnotationsForPage(pageNumber, serializedPageNormalized);
      const hash = this.getAnnotationsHash();
      if (hash !== this.annotationsHash) {
        this.annotationsHash = hash;
        this.eventBus.dispatch("progress_state", { text: "Saving annotations" });
        const editUrl = this._archivedFileId
          ? API_URL.ANNOTATIONS.editPdfAnnotationsArchivFile(
              projectsDataStore.projectId,
              this._fileId,
              this._archivedFileId
            )
          : API_URL.ANNOTATIONS.editPdfAnnotation(projectsDataStore.projectId, this._fileId);
        await API.editData(editUrl, this.annotationsDb, {
          worklistId: this.worklistId,
        });
        await this.refetchAnnotations();

        this.eventBus.dispatch("progress_state", { text: "Annotations saved", keep: 3000 });
      }
    } finally {
      this.loading = false;
    }
  }

  // returns serialized page with objects that are transformed according to "deltaScale" and "deltaRotation" values
  _getTransformedSerializedObjects(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    serializedCanvas: any, // the result of fabric.Canvas.toObject function
    deltaScale: number,
    deltaRotation: number,
    canvasSize: { width: number; height: number },
    options?: TransformSerializedAnnotationCanvasOptions
  ) {
    return transformSerializedAnnotationCanvas(serializedCanvas, deltaScale, deltaRotation, canvasSize, options);
  }

  /**
   * Scales and rotates a copy of the stored annotations up to what the canvas currently displays.
   *
   * Stored annotations are always kept at scale 1 in the page's unrotated orientation, so their stored
   * page dimensions - not the zoomed canvas - describe the space their coordinates are in.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _getSerializedObjectsForDisplay(storedAnnotations: any, canvas: ExtendedFabricCanvas) {
    const deltaScale = canvas.scale; // because stored canvas scale is always 1
    const deltaRotation = canvas.rotation; // because stored canvas rotation is always 0
    const fallbackSize =
      deltaScale > 0
        ? { width: canvas.width / deltaScale, height: canvas.height / deltaScale }
        : { width: canvas.width, height: canvas.height };

    return this._getTransformedSerializedObjects(
      storedAnnotations,
      deltaScale,
      deltaRotation,
      resolveStoredCanvasSize(storedAnnotations, fallbackSize),
      { writePageDimensions: false }
    );
  }

  /** @tool - if present it will be reset only if this is currently active tool. Otherwise any tool will be reset */
  resetTool(tool?: AnnotationTool) {
    if (!tool || tool === this.activeTool) {
      this.activeTool = null;
      this.fabricPages.forEach((canvas) => (canvas.isDrawingMode = false));
      this._storedSignature = null;
      document.querySelectorAll(`.signature-item`)?.forEach((node) => node.classList.remove("selected"));
    }
  }

  setTool({ tool }) {
    console.log("tool", tool);
    if (
      (this.activeTool === AnnotationTool.PENCIL || this.activeTool === AnnotationTool.HIGHLIGHTER) &&
      tool !== this.activeTool
    ) {
      this.fabricPages.forEach((canvas) => (canvas.isDrawingMode = false));
    }
    if (this.activeTool !== null && this.activeTool === tool) {
      this.resetTool();
    } else {
      this.activeTool = tool;
      if (!this.enabled) {
        this.setAnnotationMode(true);
      }
    }
    this._clearToggledClasses();
    if (!this.activeTool) {
      return;
    }
    let activeToolElement: Element;
    if (tool === AnnotationTool.TEXT) {
      activeToolElement = document.getElementById("textTool");
    } else if (tool === AnnotationTool.RECT) {
      activeToolElement = document.getElementById("rectTool");
    } else if (tool === AnnotationTool.LINE) {
      activeToolElement = document.getElementById("lineTool");
    } else if (tool === AnnotationTool.CHECK) {
      activeToolElement = document.getElementById("checkTool");
    } else if (tool === AnnotationTool.CROSS) {
      activeToolElement = document.getElementById("crossTool");
    } else if (tool === AnnotationTool.SIGNATURE) {
      activeToolElement = document.getElementById("signatureTool");
    } else if (tool === AnnotationTool.PENCIL) {
      activeToolElement = document.getElementById("pencilTool");
      this.fabricPages.forEach((canvas) => {
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.width = this.annotationSettings.pencilWidth;
        canvas.freeDrawingBrush.color = this.annotationSettings.fill;
      });
      this.fabricPages.forEach((canvas) => (canvas.isDrawingMode = true));
    } else if (tool === AnnotationTool.ERASER) {
      activeToolElement = document.getElementById("eraserTool");
      this.fabricPages.forEach((canvas) => {
        canvas.isDrawingMode = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canvas.freeDrawingBrush = new (fabric as any).EraserBrush(canvas);
        canvas.freeDrawingBrush.width = this.annotationSettings.pencilWidth;
        canvas.freeDrawingBrush.color = this.annotationSettings.fill;
      });
    } else if (tool === AnnotationTool.HIGHLIGHTER) {
      activeToolElement = document.getElementById("highlighterTool");
      this.fabricPages.forEach((canvas) => {
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.width = this.annotationSettings.highlightWidth;
        canvas.freeDrawingBrush.color = this.annotationSettings.highlightColor;
      });
      this.fabricPages.forEach((canvas) => (canvas.isDrawingMode = true));
    }
    if (activeToolElement) {
      activeToolElement.setAttribute("aria-checked", "true");
      activeToolElement.classList.add("toggled");

      document.querySelectorAll("._dropdownToggler.toggled").forEach((node) => {
        if (!node.isSameNode(activeToolElement)) {
          node.classList.remove("toggled");
        }
      });
    }
  }

  setAnnotationMode(enabled: boolean) {
    this.enabled = enabled;

    if (this.enabled) {
      // to be sure that "grab" tool is turned off
      this.eventBus.dispatch("switchcursortool", { tool: CursorTool.SELECT, silent: true });
      document.getElementById("cursorSelectTool").classList.remove("toggled");
      document.getElementById("pdfViewer").classList.add("can-draw");
      document.getElementById("annotationMode").classList.add("toggled");
      document.removeEventListener("keydown", this.boundKeyboardEventListener);
      document.addEventListener("keydown", this.boundKeyboardEventListener);
    } else {
      document.getElementById("pdfViewer").classList.remove("can-draw");
      document.getElementById("annotationMode").classList.remove("toggled");
      this._clearToggledClasses(); // to remove all active tool buttons
      this.fabricPages.forEach((canvas) => (canvas.isDrawingMode = false));
      this.activeTool = null; // to discard selected tool
      document.removeEventListener("keydown", this.boundKeyboardEventListener);
    }
  }

  setTextToolOptions(details: Partial<ITextOptions>) {
    Object.entries(details).forEach(([key, value]) => {
      this.annotationSettings[key] = value;
    });
    this.saveAnnotationSettings();
  }

  turnOff({ tool, silent }) {
    if (this.enabled) {
      if (!silent) {
        this.setAnnotationMode(false);
      }
      this.eventBus.dispatch("cursortoolchanged", { tool: tool });
      this._deselectAll();
    }
  }

  setFreeDrawWidth(value: number, tool: AnnotationTool.PENCIL | AnnotationTool.HIGHLIGHTER) {
    if (tool === AnnotationTool.HIGHLIGHTER) {
      this.annotationSettings.highlightWidth = value;
    } else {
      this.annotationSettings.pencilWidth = value;
    }
    this.saveAnnotationSettings();
    if (this.activeTool === tool) {
      this.fabricPages.forEach((canvas) => (canvas.freeDrawingBrush.width = value));
    }
  }

  setColor(value: string, tool: AnnotationTool.PENCIL | AnnotationTool.HIGHLIGHTER) {
    if (tool === AnnotationTool.HIGHLIGHTER) {
      this.annotationSettings.highlightColor = value;
    } else {
      this.annotationSettings.fill = value;
    }
    this.saveAnnotationSettings();
    if (this.activeTool === tool) {
      this.fabricPages.forEach((canvas) => (canvas.freeDrawingBrush.color = value));
    }
  }

  setSignature(signatureObject: fabric.Object, id: string) {
    this._storedSignature = signatureObject;
    document.querySelectorAll(`.signature-item`)?.forEach((node) => node.classList.remove("selected"));
    document.getElementById(id)?.classList.add("selected");
  }

  async mergeAnnotations() {
    const okHandler = async () => {
      try {
        if (this.loading) {
          await until(() => !this.loading, 250, 15000);
        }
        document.getElementById("pdf_wrapper").classList.add("loading");
        const url = this._archivedFileId
          ? API_URL.ANNOTATIONS.createArchivedAnnotatedPdf(
              projectsDataStore.projectId,
              this._fileId,
              this._archivedFileId
            )
          : API_URL.ANNOTATIONS.createAnnotatedPdf(projectsDataStore.projectId, this._fileId);
        await API.getBlobByPost(url, null, {
          worklistId: this.worklistId,
        });
        pdfViewerStore.reFetch();
      } catch (error) {
        notificationError(
          i18n.t("Error", { ns: I18N_NS.PROJECT }) as string,
          i18n.t("Sorry something went wrong.", { ns: I18N_NS.GENERAL }) as string
        );
      } finally {
        document.getElementById("pdf_wrapper")?.classList.remove("loading");
      }
    };
    modal.confirm({
      content: i18n.t(
        // eslint-disable-next-line max-len
        "This will replace the current version of the file with a new one. The new version will include all currently visible annotations. Do you want to continue?",
        { ns: I18N_NS.PROJECT }
      ) as string,
      okText: i18n.t("Yes", { ns: I18N_NS.PROJECT }) as string,
      cancelText: i18n.t("No", { ns: I18N_NS.PROJECT }) as string,
      zIndex: 1070,
      onOk: okHandler,
    });
  }

  keyboardEventListener(e: KeyboardEvent) {
    const target = e.target as HTMLElement | null;
    // Do not handle hotkeys while user types in any editable element.
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
      return;
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      Array.from(this.fabricPages.values()).forEach((canvas) => {
        const activeObjects = canvas.getActiveObjects();
        activeObjects.forEach((o) => {
          if ((o as fabric.IText).isEditing) {
            (o as fabric.IText).exitEditing();
          }
        });
        canvas.remove(...activeObjects);
        canvas.discardActiveObject().renderAll();
        if (activeObjects.length) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this._preserveChanges((canvas as ExtendedFabricCanvas).pageNumber, { ctx: (canvas as any).contextTop });
        }
      });
    } else if (e.ctrlKey) {
      if (e.key.toLowerCase() === "c") {
        if (this.selectedCanvasPageNumber) {
          const canvasPage = this.fabricPages.get(this.selectedCanvasPageNumber);
          if (canvasPage) {
            canvasPage.getActiveObject()?.clone((cloned) => {
              this.selectedCanvasObject = cloned;
            });
          }
        }
      } else if (e.key.toLowerCase() === "v") {
        if (!this.selectedCanvasObject) {
          return;
        }
        const pastingPage = this.fabricPages.get(this.mousePosition.pageNumber);
        if (pastingPage) {
          this.selectedCanvasObject.clone((clonedObj) => {
            clonedObj.set({
              left: this.mousePosition.pointer.x,
              top: this.mousePosition.pointer.y,
              evented: true,
            });
            if (clonedObj.type === "activeSelection") {
              // active selection needs a reference to the canvas.
              clonedObj.canvas = pastingPage;
              clonedObj.forEachObject((obj) => {
                pastingPage.add(obj);
              });
              // this should solve the unselectability
              clonedObj.setCoords();
            } else {
              pastingPage.add(clonedObj);
            }
            pastingPage.setActiveObject(clonedObj);
            pastingPage.requestRenderAll();
          });
        }
      }
    }
  }

  _deselectAll() {
    Array.from(this.fabricPages.values()).forEach((canvas) => {
      canvas.discardActiveObject();
      canvas.renderAll();
    });
  }

  destroy() {
    this._fileId = null;
    this._archivedFileId = null;
    this.fetchOptions = {};
    this.worklistId = null;
    this.eventBus._off("pagerendered", this.renderCanvasForPage.bind(this));
    this.eventBus._off("switchcursortool", this.turnOff.bind(this));
    this.eventBus._off("setannotationtool", this.setTool.bind(this));
    this.eventBus._off("resetannotationtool", this.resetTool.bind(this));
    this.eventBus._off("mergeAnnotations", this.mergeAnnotations.bind(this));
    for (const canvas of Array.from(this.fabricPages.values())) {
      canvas.dispose();
    }
    this.fabricPages.clear();
    this.activeTool = null;
    this.enabled = false;
    this.annotationsDb = {};
    this.annotationsHash = null;
    this._storedSignature = null;
    this.selectedCanvasPageNumber = null;
    this.selectedCanvasObject = null;
  }

  getAnnotationsHash() {
    return hashSum(JSON.stringify(this.annotationsDb));
  }

  _clearToggledClasses() {
    document.querySelectorAll(".annotation_tool").forEach((element) => {
      element.classList.remove("toggled");
      element.setAttribute("aria-checked", "false");
    });
  }
}
